import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
  { value: "essential-due-diligence", label: "Essential Due Diligence" },
  { value: "comprehensive-advisory", label: "Comprehensive Legal Advisory" },
  { value: "diaspora-package", label: "Diaspora Investor Package" },
  { value: "custom-engagement", label: "Custom Engagement" },
];

export const BookingSection = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    service_type: "",
    message: "",
  });
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableTimes(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableTimes = async (date) => {
    const dateStr = date.toISOString().split("T")[0];
    try {
      const response = await axios.get(`${API}/bookings/available-times/${dateStr}`);
      setAvailableTimes(response.data.available_times);
      setSelectedTime("");
    } catch (error) {
      console.error("Error fetching available times:", error);
      setAvailableTimes([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime || !formData.service_type) {
      toast.error("Please select a date, time, and service type");
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        ...formData,
        booking_date: selectedDate.toISOString().split("T")[0],
        booking_time: selectedTime,
      };

      await axios.post(`${API}/bookings`, bookingData);
      
      setBookingSuccess(true);
      toast.success("Consultation request submitted successfully!");
      
      setFormData({
        client_name: "",
        client_email: "",
        client_phone: "",
        service_type: "",
        message: "",
      });
      setSelectedDate(null);
      setSelectedTime("");
      
      setTimeout(() => setBookingSuccess(false), 5000);
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const disablePastDates = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0;
  };

  return (
    <section
      id="booking"
      className="py-24 lg:py-32 bg-white border-t border-[#E5E5E5]"
      data-testid="booking-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#D4AF37] text-sm tracking-[0.25em] uppercase font-medium">
            Schedule a Consultation
          </span>
          <h2
            className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] mt-4 mb-6"
            data-testid="booking-heading"
          >
            Request Your Consultation
          </h2>
          <p className="text-[#1A1A1A]/60 text-lg">
            Select your preferred date and time for a consultation with our legal team.
          </p>
        </div>

        {bookingSuccess && (
          <div className="max-w-3xl mx-auto mb-8 p-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center gap-4" data-testid="booking-success">
            <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
            <div>
              <h4 className="font-semibold text-[#1A1A1A]">Request Submitted</h4>
              <p className="text-[#1A1A1A]/70">We'll confirm your appointment within 24 hours.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto" data-testid="booking-form">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calendar Side */}
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CalendarDays className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="font-semibold text-[#1A1A1A]">Select Date</h3>
                </div>
                <div className="bg-[#FAFAF9] p-4 border border-[#E5E5E5]">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disablePastDates}
                    className="mx-auto"
                    data-testid="booking-calendar"
                  />
                </div>
              </div>

              {selectedDate && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                    <h3 className="font-semibold text-[#1A1A1A]">Select Time</h3>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="time-slots">
                    {availableTimes.length > 0 ? (
                      availableTimes.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setSelectedTime(time)}
                          className={`px-4 py-3 text-sm font-medium border transition-all ${
                            selectedTime === time
                              ? "bg-[#D4AF37] text-[#0A0A0B] border-[#D4AF37]"
                              : "bg-white text-[#1A1A1A] border-[#E5E5E5] hover:border-[#D4AF37]"
                          }`}
                          data-testid={`time-slot-${time}`}
                        >
                          {time}
                        </button>
                      ))
                    ) : (
                      <p className="col-span-full text-[#1A1A1A]/50 text-sm">
                        No available times for this date
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Form Side */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                  data-testid="booking-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="client_email"
                  value={formData.client_email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Enter your email"
                  data-testid="booking-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="client_phone"
                  value={formData.client_phone}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="+234..."
                  data-testid="booking-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Service Required *
                </label>
                <Select onValueChange={handleServiceChange} value={formData.service_type}>
                  <SelectTrigger 
                    className="form-input h-auto"
                    data-testid="booking-service-select"
                  >
                    <SelectValue placeholder="Select a service package" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-[#E5E5E5]">
                    {serviceTypes.map((service) => (
                      <SelectItem 
                        key={service.value} 
                        value={service.value}
                        className="hover:bg-[#D4AF37]/10"
                      >
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Brief Description of Your Matter
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="form-input resize-none"
                  placeholder="Please provide a brief overview of your property investment needs..."
                  data-testid="booking-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !selectedDate || !selectedTime}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white px-8 py-4 text-sm tracking-[0.1em] font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#0A0A0B] transition-all"
                data-testid="booking-submit-button"
              >
                {loading ? "Submitting..." : "Request Consultation"}
                <ArrowRight className="w-4 h-4" />
              </button>
              
              <p className="text-[#1A1A1A]/50 text-xs text-center">
                We will confirm your appointment within 24 hours via email.
              </p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};
